import React, { useState } from "react";
import { MapPinOff, X } from "lucide-react";
import { useLocation } from "@/context/LocationContext";
import { toast } from "sonner";

// Shown at the top of every page when the browser has denied location access.
// Gives the user a manual escape hatch — enter a 6-digit Indian pincode OR a city
// name and we geocode it via Nominatim. Falls back gracefully; never blocks the UI.

const isPincode = (v) => /^\d{6}$/.test(String(v).trim());

export default function GeoDeniedBanner() {
  const { permission, setCity } = useLocation();
  const [entry, setEntry] = useState("");
  const [saving, setSaving] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem("find2near:geo-banner-dismissed") === "1"; } catch { return false; }
  });

  if (permission !== "denied" || dismissed) return null;

  const dismiss = () => {
    try { sessionStorage.setItem("find2near:geo-banner-dismissed", "1"); } catch {}
    setDismissed(true);
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    const raw = entry.trim();
    if (!raw) { toast.error("Enter your pincode or city"); return; }
    setSaving(true);
    try {
      // Pincode → Nominatim postal search, else free-text city search.
      const q = isPincode(raw) ? `${raw} India` : raw;
      const r = await fetch(
        `https://nominatim.openstreetmap.org/search?format=jsonv2&limit=1&countrycodes=in&q=${encodeURIComponent(q)}`,
        { headers: { "Accept-Language": "en" } }
      );
      if (r.ok) {
        const arr = await r.json();
        if (arr && arr[0]) {
          const cityLabel = arr[0].display_name?.split(",")[0] || raw;
          await setCity(cityLabel, parseFloat(arr[0].lat), parseFloat(arr[0].lon));
          toast.success(`Location set to ${cityLabel}`);
          dismiss();
          return;
        }
      }
      toast.error("Could not find that pincode/city — try a bigger locality name");
    } catch {
      toast.error("Network hiccup — please try again");
    } finally { setSaving(false); }
  };

  return (
    <div
      className="w-full bg-amber-50 border-b border-amber-200 text-amber-900 px-4 py-2"
      data-testid="geo-denied-banner"
      role="status"
    >
      <form onSubmit={submit} className="max-w-6xl mx-auto flex items-center gap-2 flex-wrap">
        <MapPinOff size={16} className="shrink-0" />
        <span className="text-xs md:text-sm font-semibold flex-1 min-w-[180px]">
          Location access blocked. Enter your 6-digit pincode or city so we can show nearby results.
        </span>
        <input
          value={entry}
          onChange={(e) => setEntry(e.target.value)}
          placeholder="560001 or Bengaluru"
          className="rounded-full border border-amber-300 bg-white text-stone-800 text-sm px-3 py-1.5 h-[36px] min-w-[10rem] focus:outline-none focus:ring-1 focus:ring-amber-400"
          data-testid="geo-denied-input"
        />
        <button
          type="submit"
          disabled={saving}
          className="rounded-full bg-amber-500 hover:bg-amber-600 text-white text-xs font-black uppercase tracking-widest px-4 h-[36px] disabled:opacity-60"
          data-testid="geo-denied-submit"
        >
          {saving ? "Saving…" : "Set"}
        </button>
        <button
          type="button"
          onClick={dismiss}
          className="text-amber-600 hover:text-amber-900 w-[36px] h-[36px] grid place-items-center rounded-full"
          aria-label="Dismiss"
          data-testid="geo-denied-dismiss"
        >
          <X size={16} />
        </button>
      </form>
    </div>
  );
}
